import {
  Form,
  SecondaryButton,
  SecondaryActionsButton,
  PrimaryButton,
  NegativeButton,
} from "common/Forms";
import { SelectInput } from "common/Forms/inputs";
import { ModalDialog } from "common/Dialog";
import { useState } from "react";
import { InRow, ResponsiveGap } from "common/Grid";
import { Grid, GridCell } from "common/Grid/Grid";
import { TableRenderer } from "common/Table";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { captureComponentError } from "utils/sentry";
import { useLeadApproveDemoMutation, useLeadApproveLiveMutation, useUpdateSalesLeadMutation } from "store/action/salesLeadsApi";
import { buildRequestSalesLeadPath, buildEditSalesLeadsPath, buildEditRequestSalesLeadPath } from "app-router/paths";
import { PermissionKeys, siteRequestStatus, userRoles, SALES_LEAD_STATUS } from "Constant/Constant";
import {
  DefaultStatusLabel,
  NegativeStatusLabel,
  PositiveStatusLabel,
  WarningStatusLabel,
  ProgressStatusLabel,
  OrangeStatusLabel,
  DarkRedStatusLabel,
} from "common/Label";
import { IfHasPermission, usePermissions } from "context/PermissionsContext";
import { BiStoreAlt, BiBuildings } from "react-icons/bi";
import sortAsc from "assets/img/sort-asc.svg";
import sortDesc from "assets/img/sort-desc.svg";
import { LuPencilLine, LuCheck, LuX, LuSendHorizontal, LuPencil } from "react-icons/lu";
import { FiSettings } from "react-icons/fi";

const APPROVE = 1;
const REJECT = 2;

const SortHeader = ({ label, sortByValue, sorting, onSortChange }) => {
  const isAscActive = sorting?.sortBy === sortByValue && sorting?.sortOrder === 1;
  const isDescActive = sorting?.sortBy === sortByValue && sorting?.sortOrder === 2;
  return (
    <div className="flex items-center justify-between w-full">
      <span>{label}</span>
      <div className="flex items-center gap-1">
        <img
          src={sortAsc}
          alt="sort asc"
          onClick={() => onSortChange?.(sortByValue, 1)}
          className={`cursor-pointer transition-opacity hover:opacity-100 ${isAscActive ? "opacity-100" : "opacity-35"}`}
          style={{ width: 20, height: 20 }}
        />
        <img
          src={sortDesc}
          alt="sort desc"
          onClick={() => onSortChange?.(sortByValue, 2)}
          className={`cursor-pointer transition-opacity hover:opacity-100 ${isDescActive ? "opacity-100" : "opacity-35"}`}
          style={{ width: 20, height: 20 }}
        />
      </div>
    </div>
  );
};

export const TableView = ({ salesLeadsList, sorting, onSortChange }) => {

  const navigate = useNavigate();
  const { allowedKeys, role, userId } = usePermissions();
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false);
  const [setupLead, setSetupLead] = useState(null);
  const [setupType, setSetupType] = useState("demo");
  const [setupMode, setSetupMode] = useState("single");
  const [franchiseCount, setFranchiseCount] = useState(1);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    lead: null,
    status: null,
    type: "",
    rejectionReason: "",
  });

  const [updateSalesLead, { isLoading: isUpdating }] = useUpdateSalesLeadMutation();
  const handleSetupConfirm = async () => {
    try {
      const isFranchise = setupMode === "franchise";
      const count = isFranchise ? franchiseCount : 1;

      await updateSalesLead({
        body: {
          _id: setupLead._id,
          isFranchise,
          franchiseCount: count ?? 1,
        },
      }).unwrap();

      setIsSetupModalOpen(false);
      navigate(buildRequestSalesLeadPath(setupType), {
        state: {
          lead: setupLead,
          isFranchise,
          franchiseCount: count,
        },
      });
    } catch (error) {
      captureComponentError(error, {
        component: "TableView",
        action: "handleSetupConfirm",
      });
      toast.error(error?.data?.message || "Something went wrong!");
    }
  };

  const [leadApproveDemo, { isLoading: isDemoApproving }] = useLeadApproveDemoMutation();
  const [leadApproveLive, { isLoading: isLiveApproving }] = useLeadApproveLiveMutation();

  const handleApproveRejectDemo = async (lead, status) => {
    try {
      const isApproved = status === APPROVE;
      const response = await leadApproveDemo({
        body: {
          isApproved,
          salesLeadId: lead._id,
          ...(status === REJECT ? { demoRejectionReason: lead.rejectionReason } : {}),
        },
      }).unwrap();
      toast.success(
        response?.message || (status === APPROVE ? "Demo approved" : "Demo rejected"),
      );
    } catch (error) {
      captureComponentError(error, {
        component: "TableView",
        action: "handleApproveRejectDemo",
      });
      toast.error(error?.data?.message || "Something went wrong!");
    }
  };

  const handleApproveRejectLive = async (lead, status) => {
    try {
      const isApproved = status === APPROVE;
      const response = await leadApproveLive({
        body: {
          isApproved,
          salesLeadId: lead._id,
          ...(status === REJECT ? { liveRejectionReason: lead.rejectionReason } : {}),
        },
      }).unwrap();
      toast.success(
        response?.message || (status === APPROVE ? "Live approved" : "Live rejected"),
      );
    } catch (error) {
      captureComponentError(error, {
        component: "TableView",
        action: "handleApproveRejectLive",
      });
      toast.error(error?.data?.message || "Something went wrong!");
    }
  };

  const onActionConfirm = () => {
    const leadWithReason = {
      ...confirmModal.lead,
      rejectionReason: confirmModal.rejectionReason,
    };
    if (confirmModal.type === "demo") {
      handleApproveRejectDemo(leadWithReason, confirmModal.status);
    } else {
      handleApproveRejectLive(leadWithReason, confirmModal.status);
    }
    setConfirmModal((prev) => ({ ...prev, isOpen: false, rejectionReason: "" }));
  };

  const meatballClassNames = [
    "!bg-secondary-secondary_button",
    "!p-1",
    "[&>div>div:nth-child(2)]:!hidden",
  ];
  const canApproveRejectDemo =
    (role === userRoles.ADMIN) || allowedKeys.includes(PermissionKeys.APPROVE_REJECT_DEMO);
  const canApproveRejectLive =
    (role === userRoles.ADMIN) || allowedKeys.includes(PermissionKeys.APPROVE_REJECT_LIVE);
  const canRequestDemo =
    (role === userRoles.ADMIN) || allowedKeys.includes(PermissionKeys.REQUEST_DEMO);
  const canRequestLive =
    (role === userRoles.ADMIN) || allowedKeys.includes(PermissionKeys.REQUEST_LIVE);
  const renderDemoButtons = (x) => {
    if (siteRequestStatus.NOT_REQUESTED === x?.requestDemoStatus) {
      return (
        <IfHasPermission
          permissionKey={PermissionKeys.REQUEST_DEMO}
          fallback={<DefaultStatusLabel title="-" />}
        >
          <SecondaryActionsButton
            classNames={meatballClassNames}
            optionsWidth="centered"
            disabled={x?.status === SALES_LEAD_STATUS.CANCELED}
            title="Options"
            actions={[
              {
                content: (
                  <span className="flex items-center gap-2 text-blue-600">
                    <LuSendHorizontal size={15} />
                    Request Demo
                  </span>
                ),
                action: () => {
                  if (x?.franchiseCount && x?.requestDemo?.length > 0) {
                    navigate(buildRequestSalesLeadPath("demo"), {
                      state: {
                        lead: x,
                        isFranchise: x.isFranchise,
                        franchiseCount: x.franchiseCount,
                      },
                    });
                  } else {
                    setSetupLead(x);
                    setSetupType("demo");
                    setSetupMode(x?.isFranchise ? "franchise" : "single");
                    setFranchiseCount(x?.franchiseCount || 1);
                    setIsSetupModalOpen(true);
                  }
                },
              },
              {
                content: "",
                classNames: ["!hidden"],
              },
            ]}>
            <FiSettings size={20} />
          </SecondaryActionsButton>
        </IfHasPermission>
      );
    }

    switch (x?.requestDemoStatus) {
      case siteRequestStatus.SITE_REQUESTED:
        if (canApproveRejectDemo || canRequestDemo) {
          return (
            <SecondaryActionsButton
              classNames={meatballClassNames}
              optionsWidth="centered"
              disabled={x?.status === SALES_LEAD_STATUS.CANCELED}
              actions={[
                ...(canApproveRejectDemo
                  ? [
                    {
                      content: (
                        <span className="flex items-center gap-2 text-green-600">
                          <LuCheck size={15} />
                          Approve Demo
                        </span>
                      ),
                      action: () =>
                        setConfirmModal({
                          isOpen: true,
                          title: "Confirm Approval",
                          message: "Are you sure you want to approve this demo request?",
                          lead: x,
                          status: APPROVE,
                          type: "demo",
                          rejectionReason: "",
                        }),
                      disabled: isDemoApproving,
                    },
                    {
                      content: (
                        <span className="flex items-center gap-2 text-red-500">
                          <LuX size={15} />
                          Reject Demo
                        </span>
                      ),
                      action: () =>
                        setConfirmModal({
                          isOpen: true,
                          title: "Confirm Rejection",
                          message: "Are you sure you want to reject this demo request?",
                          lead: x,
                          status: REJECT,
                          type: "demo",
                          rejectionReason: "",
                        }),
                      disabled: isDemoApproving,
                    },
                  ]
                  : []),
                ...(canRequestDemo ? [
                  {
                    content: (
                      <span className="flex items-center gap-2 text-gray-600">
                        <LuPencil size={15} />
                        Edit Request Demo
                      </span>
                    ),
                    action: () => {
                      navigate(buildEditRequestSalesLeadPath("demo", x._id));
                    },
                  },
                ] : []),
                {
                  content: "",
                  classNames: ["!hidden"],
                },
              ]}>
              <FiSettings size={20} />
            </SecondaryActionsButton>
          );
        }
        return <DefaultStatusLabel title="-" />;

      case siteRequestStatus.APPROVED:
        return <PositiveStatusLabel title="Approved" />;

      case siteRequestStatus.REJECTED:
        return <NegativeStatusLabel title="Rejected" />;

      case siteRequestStatus.GENERATING:
        return <WarningStatusLabel title="Generating..." />;

      default:
        return <DefaultStatusLabel title={"-"} />;
    }
  };

  const renderLiveButtons = (x) => {
    if (siteRequestStatus.NOT_REQUESTED === x?.requestLiveStatus) {
      return (
        <IfHasPermission
          permissionKey={PermissionKeys.REQUEST_LIVE}
          fallback={<DefaultStatusLabel title="-" />}
        >
          <SecondaryActionsButton
            classNames={meatballClassNames}
            disabled={x?.status === SALES_LEAD_STATUS.CANCELED}
            optionsWidth="centered"
            title="Options"
            actions={[
              {
                content: (
                  <span className="flex items-center gap-2 text-blue-600">
                    <LuSendHorizontal size={15} />
                    Request Live
                  </span>
                ),
                action: () => {
                  const hasLiveDraft = x?.franchiseCount && x?.requestLive?.length > 0;
                  const hasDemoRequested = x?.requestDemoStatus !== siteRequestStatus.NOT_REQUESTED;
                  if (hasLiveDraft || hasDemoRequested) {
                    navigate(buildRequestSalesLeadPath("live"), {
                      state: {
                        lead: x,
                        isFranchise: x.isFranchise,
                        franchiseCount: x.franchiseCount || 1,
                      },
                    });
                  } else {
                    setSetupLead(x);
                    setSetupType("live");
                    setSetupMode(x?.isFranchise ? "franchise" : "single");
                    setFranchiseCount(x?.franchiseCount || 1);
                    setIsSetupModalOpen(true);
                  }
                },
              },
              {
                content: "",
                classNames: ["!hidden"],
              },
            ]}>
            <FiSettings size={20} />
          </SecondaryActionsButton>
        </IfHasPermission>
      );
    }

    switch (x?.requestLiveStatus) {
      case siteRequestStatus.SITE_REQUESTED:
        if (canApproveRejectLive || canRequestLive) {
          return (
            <SecondaryActionsButton
              classNames={meatballClassNames}
              disabled={x?.status === SALES_LEAD_STATUS.CANCELED}
              optionsWidth="centered"
              actions={[
                ...(canApproveRejectLive
                  ? [
                    {
                      content: (
                        <span className="flex items-center gap-2 text-green-600">
                          <LuCheck size={15} />
                          Approve Live
                        </span>
                      ),
                      action: () =>
                        setConfirmModal({
                          isOpen: true,
                          title: "Confirm Approval",
                          message: "Are you sure you want to approve this live request?",
                          lead: x,
                          status: APPROVE,
                          type: "live",
                          rejectionReason: "",
                        }),
                      disabled: isLiveApproving,
                    },
                    {
                      content: (
                        <span className="flex items-center gap-2 text-red-500">
                          <LuX size={15} />
                          Reject Live
                        </span>
                      ),
                      action: () =>
                        setConfirmModal({
                          isOpen: true,
                          title: "Confirm Rejection",
                          message: "Are you sure you want to reject this live request?",
                          lead: x,
                          status: REJECT,
                          type: "live",
                          rejectionReason: "",
                        }),
                      disabled: isLiveApproving,
                    },
                  ]
                  : []),
                ...(canRequestLive ? [
                  {
                    content: (
                      <span className="flex items-center gap-2 text-gray-600">
                        <LuPencil size={15} />
                        Edit Request Live
                      </span>
                    ),
                    action: () => {
                      navigate(buildEditRequestSalesLeadPath("live", x._id));
                    },
                  },
                ] : []),
                {
                  content: "",
                  classNames: ["!hidden"],
                },
              ]}>
              <FiSettings size={20} />
            </SecondaryActionsButton>
          );
        }
        return <DefaultStatusLabel title="-" />;

      case siteRequestStatus.APPROVED:
        return <PositiveStatusLabel title="Approved" />;

      case siteRequestStatus.REJECTED:
        return <NegativeStatusLabel title="Rejected" />;

      case siteRequestStatus.GENERATING:
        return <WarningStatusLabel title="Generating..." />;

      default:
        return <DefaultStatusLabel title={"-"} />;
    }
  };

  const hasPermission = (permissionKey) => {
    return role === "Admin" || allowedKeys.includes(permissionKey);
  }

  return (
    <Form>
      <ModalDialog
        isOpened={isSetupModalOpen}
        title={`Request ${setupType === "demo" ? "Demo" : "Live"}`}
        onClose={() => setIsSetupModalOpen(false)}
        footer={
          <InRow gap={1} classNames={["justify-end w-full px-2 pb-2"]}>
            <SecondaryButton action={() => setIsSetupModalOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton action={handleSetupConfirm} disabled={isUpdating}>Next</PrimaryButton>
          </InRow>
        }>
        <div className="flex flex-col gap-6 min-w-[450px] p-2">
          <div className="text-center mb-2">
            <h3 className="text-lg font-bold text-gray-800">Select Request Type</h3>
            <p className="text-sm text-gray-500">Choose how you want to configure this site</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div
              onClick={() => setSetupMode("single")}
              className={`cursor-pointer p-6 rounded-2xl border-2 transition-all duration-300 flex flex-col items-center gap-3 ${setupMode === "single"
                ? "border-secondary-primary_button_bg bg-secondary-secondary_button/30 shadow-sm"
                : "border-gray-100 hover:border-gray-200 bg-gray-50/50"
                }`}>
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${setupMode === "single"
                  ? "bg-secondary-primary_button_bg text-white"
                  : "bg-gray-200 text-gray-500"
                  }`}>
                <BiStoreAlt size={24} />
              </div>
              <div className="text-center">
                <p className="font-bold text-gray-800">Single Site</p>
                <p className="text-xs text-gray-500 mt-1">Configure one site</p>
              </div>
            </div>

            <div
              onClick={() => setSetupMode("franchise")}
              className={`cursor-pointer p-6 rounded-2xl border-2 transition-all duration-300 flex flex-col items-center gap-3 ${setupMode === "franchise"
                ? "border-secondary-primary_button_bg bg-secondary-secondary_button/30 shadow-sm"
                : "border-gray-100 hover:border-gray-200 bg-gray-50/50"
                }`}>
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${setupMode === "franchise"
                  ? "bg-secondary-primary_button_bg text-white"
                  : "bg-gray-200 text-gray-500"
                  }`}>
                <BiBuildings size={24} />
              </div>
              <div className="text-center">
                <p className="font-bold text-gray-800">Franchise</p>
                <p className="text-xs text-gray-500 mt-1">Multiple sites setup</p>
              </div>
            </div>
          </div>

          {setupMode === "franchise" && (
            <div className="flex flex-col gap-3 mt-2 p-4 bg-gray-50 rounded-xl border border-gray-100 transition-all duration-500 animate-in fade-in slide-in-from-top-2">
              <label className="text-sm font-bold text-gray-700 flex items-center justify-between">
                Number of Franchise Sites
              </label>
              <SelectInput
                id="franchiseCount"
                selectedKey={String(franchiseCount)}
                optionProvider={() =>
                  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({ label: `${n} Sites`, value: String(n) }))
                }
                keySelector={(x) => x.value}
                labelSelector={(x) => x.label}
                onChange={(_, n) => setFranchiseCount(Number(n?.value || 1))}
              />
            </div>
          )}
        </div>
      </ModalDialog>

      <ModalDialog
        isOpened={confirmModal.isOpen}
        title={confirmModal.title}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}>
        <div className="space-y-6">
          <div className="text-center text-gray-700 font-medium text-lg leading-relaxed px-4">
            {confirmModal.message}
          </div>
          {confirmModal.status === REJECT && (
            <div className="px-2">
              <label className="text-sm font-bold text-gray-500 mb-2 block text-left tracking-wider">
                Rejection Reason :
              </label>
              <div className="relative">
                <textarea
                  placeholder="Enter reason for rejection"
                  maxLength={250}
                  className="w-full h-24 rounded-xl p-4 border-gray-100 border-[1px] bg-white focus:border-secondary-primary_button_bg transition-all outline-none shadow-sm text-[16px] font-medium leading-5 placeholder:text-gray-400 placeholder:font-normal resize-none"
                  value={confirmModal.rejectionReason}
                  onChange={(e) =>
                    setConfirmModal((prev) => ({ ...prev, rejectionReason: e.target.value }))
                  }
                />
                <div className="flex justify-between mt-1 px-1">
                  <span className="text-[10px] text-gray-400 italic">
                    Maximum 250 characters
                  </span>
                  <span className={`text-[10px] font-bold ${(confirmModal.rejectionReason?.length || 0) >= 250
                    ? "text-red-500"
                    : "text-gray-400"
                    }`}>
                    {confirmModal.rejectionReason?.length || 0}/250
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
        <ResponsiveGap left={false} right={false} classNames={["mt-8"]}>
          <Grid columns={2} gap={4}>
            <GridCell>
              {confirmModal.status === APPROVE ? (
                <PrimaryButton isWide={true} action={onActionConfirm}>
                  Approve
                </PrimaryButton>
              ) : (
                <NegativeButton
                  isWide={true}
                  action={onActionConfirm}
                  disabled={!confirmModal.rejectionReason?.trim()}>
                  Reject
                </NegativeButton>
              )}
            </GridCell>
            <GridCell>
              <SecondaryButton
                action={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
                isWide={true}>
                Cancel
              </SecondaryButton>
            </GridCell>
          </Grid>
        </ResponsiveGap>
      </ModalDialog>
      <TableRenderer
        classNames={["[&_tr:hover]:bg-gray-50"]}
        data={salesLeadsList ?? []}
        columns={[
          (hasPermission(PermissionKeys.EDIT_ANY_LEAD)) ? {
            name: "Action",
            widthRequest: { min: 100 },
            classNames: ["!text-center"],
            headerRenderer: (col) => <div className="w-full text-center">{col.name}</div>,
            bodyRenderer: (x) => (
              <InRow gap={1} classNames={["justify-center"]}>
                <SecondaryButton
                  action={() => navigate(buildEditSalesLeadsPath(x._id))}
                  title="Edit Lead"
                  classNames={["!rounded-lg !p-2 hover:!bg-blue-50 hover:!text-blue-600 transition-colors"]}>
                  <LuPencilLine size={18} />
                </SecondaryButton>
              </InRow>
            ),
          } : {
            name: "Action",
            widthRequest: { min: 100 },
            classNames: ["!text-center"],
            headerRenderer: (col) => <div className="w-full text-center">{col.name}</div>,
            bodyRenderer: (x) => (
              <InRow gap={1} classNames={["justify-center"]}>
                <SecondaryButton
                  action={() => navigate(buildEditSalesLeadsPath(x._id))}
                  title="Edit Lead"
                  disabled={role === userRoles.SALES_PERSON ? x?.assignedTo !== userId : role === userRoles.SALES_MANAGER ? x?.assignedTo !== userId && x?.createdBy !== userId : false}
                  classNames={["!rounded-lg !p-2 hover:!bg-blue-50 hover:!text-blue-600 transition-colors"]}>
                  <LuPencilLine size={18} />
                </SecondaryButton>
              </InRow>
            )
          },
          {
            name: "Demo Site",
            widthRequest: { min: 100 },
            classNames: ["!text-center"],
            headerRenderer: (col) => <div className="w-full text-center">{col.name}</div>,
            bodyRenderer: (x) => (
              <InRow gap={1} classNames={["justify-center flex-wrap"]}>
                {renderDemoButtons(x)}
              </InRow>
            ),
          },
          {
            name: "Live Site",
            widthRequest: { min: 100 },
            classNames: ["!text-center"],
            headerRenderer: (col) => <div className="w-full text-center">{col.name}</div>,
            bodyRenderer: (x) => (
              <InRow gap={1} classNames={["justify-center flex-wrap"]}>
                {renderLiveButtons(x)}
              </InRow>
            ),
          },
          {
            name: "Status",
            widthRequest: { min: 200 },
            bodyRenderer: (x) => {
              switch (x?.status?.toLowerCase()) {
                case SALES_LEAD_STATUS.COLD.toLowerCase():
                  return <ProgressStatusLabel title="Cold" />;
                case SALES_LEAD_STATUS.WARM.toLowerCase():
                  return <OrangeStatusLabel title="Warm" />;
                case SALES_LEAD_STATUS.HOT.toLowerCase():
                  return <DarkRedStatusLabel title="Hot" />;
                case SALES_LEAD_STATUS.IN_PROGRESS.toLowerCase():
                  return <WarningStatusLabel title="In Progress" />;
                case SALES_LEAD_STATUS.COMPLETED.toLowerCase():
                  return <PositiveStatusLabel title="Completed" />;
                case SALES_LEAD_STATUS.CANCELED.toLowerCase():
                  return <NegativeStatusLabel title="Canceled" />;
                default:
                  return (
                    <div className="flex items-center justify-center">
                      {x?.status ? (
                        <ProgressStatusLabel title={x.status} />
                      ) : (
                        <DefaultStatusLabel title="-" />
                      )}
                    </div>
                  );
              }
            },
          }, {
            name: "Company ID",
            widthRequest: { min: 130 },
            bodyRenderer: (x) => x?.companyId || <div>-</div>,
          },
          {
            name: "Company Name",
            widthRequest: { min: 220 },
            headerRenderer: () => <SortHeader label="Company Name" sortByValue={1} sorting={sorting} onSortChange={onSortChange} />,
            bodyRenderer: (x) => x?.name || <div>-</div>,
          },
          {
            name: "Email",
            widthRequest: { min: 220 },
            headerRenderer: () => <SortHeader label="Email" sortByValue={2} sorting={sorting} onSortChange={onSortChange} />,
            bodyRenderer: (x) => x?.email || <div>-</div>,
          },
          {
            name: "Phone",
            widthRequest: { min: 130 },
            bodyRenderer: (x) => x?.phone || <div>-</div>,
          },
          {
            name: "Industry / Category",
            widthRequest: { min: 190 },
            bodyRenderer: (x) => x?.industry || <div>-</div>,
          },
          {
            name: "Current Platform",
            widthRequest: { min: 160 },
            bodyRenderer: (x) => x?.currentPlatform || <div>-</div>,
          },
          {
            name: "Website",
            widthRequest: { min: 200 },
            bodyRenderer: (x) => x?.website || <div>-</div>,
          },
          {
            name: "Contact Person",
            widthRequest: { min: 200 },
            bodyRenderer: (x) => x?.salesRepresentative || <div>-</div>,
          },
          {
            name: "Expected Close Date",
            widthRequest: { min: 180 },
            bodyRenderer: (x) => {
              const raw = x?.expectedCloseDate;
              if (!raw) return <div>-</div>;
              const date = new Date(raw._seconds ? raw._seconds * 1000 : raw);
              return !isNaN(date) ? date.toLocaleDateString() : <div>-</div>;
            },
          },
          {
            name: "Assigned To",
            widthRequest: { min: 220 },
            headerRenderer: () => <SortHeader label="Assigned To" sortByValue={3} sorting={sorting} onSortChange={onSortChange} />,
            bodyRenderer: (x) => x?.assignedToName || <div>-</div>,
          },
          {
            name: "Address",
            widthRequest: { min: 200 },
            bodyRenderer: (x) => x?.address || <div>-</div>,
          },
          {
            name: "Estimated Annual App Sales",
            widthRequest: { min: 200 },
            bodyRenderer: (x) => x?.estimatedAnnualAppSales || <div>-</div>,
          },
          {
            name: "Proposed Fee (%)",
            widthRequest: { min: 150 },
            bodyRenderer: (x) => (x?.proposedFee ? `${x.proposedFee}%` : <div>-</div>),
          },
          {
            name: "Estimated Annual App Fee",
            widthRequest: { min: 200 },
            bodyRenderer: (x) => x?.estimatedAnnualAppFee || <div>-</div>,
          },
          {
            name: "Next Step",
            widthRequest: { min: 200 },
            bodyRenderer: (x) => x?.nextStep || <div>-</div>,
          },
        ].filter(Boolean)}
      />
    </Form >
  );
};
